// ============================================================================
//  sort.js — useSortable hook + SortableTh component
// ============================================================================
//
//  Drop-in pattern for any table:
//
//    const { sorted, sort, toggle } = useSortable(rows, 'date', 'desc');
//    <thead><tr>
//      <SortableTh k="date" sort={sort} toggle={toggle}>Date</SortableTh>
//      <SortableTh k="amount" sort={sort} toggle={toggle} className="num">Amount</SortableTh>
//    </tr></thead>
//    <tbody>{sorted.map(r => <tr>...)}</tbody>
//
//  Custom value extractor for derived fields:
//    useSortable(rows, 'date', 'desc', (r, k) => k === 'time' ? r.arrivalTime : r[k])
// ============================================================================

import { useState, useMemo } from 'react';

function defaultGet(r, k) { return r ? r[k] : undefined; }

export function useSortable(rows, defaultKey, defaultDir = 'desc', getValue) {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const get = getValue || defaultGet;
  const sorted = useMemo(() => {
    if (!rows || !rows.length) return rows || [];
    const arr = rows.slice();
    arr.sort((a, b) => {
      let av = get(a, sort.key);
      let bv = get(b, sort.key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls sort last
      if (bv == null) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, sort, get]);

  function toggle(key) {
    setSort(s => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' });   // first click on a new column → descending (most useful default)
  }

  return { sorted, sort, toggle };
}

export function SortableTh({ k, sort, toggle, children, className, style }) {
  const active = sort && sort.key === k;
  const arrow = !active ? '' : (sort.dir === 'asc' ? ' ▲' : ' ▼');
  return (
    <th
      className={className}
      onClick={() => toggle(k)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(style || {}) }}
      title="Click to sort"
    >
      {children}{arrow}
    </th>
  );
}
